import Region from '@app/model/market_state/Region';
import React from 'react';
import Tag from '@app/component/tag/Tag';
import { PromotionCountryCell, PromotionCountryOverview, PromotionCountryRow } from '@app/model/promotion/PromotionCountryOverview';
import { PreferredTierBadge }
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/tooltip/DisplayMarketStateItemPrice';
import { COUNTRY_RATE_THRESHOLD, countryCellPlans, countryCellText, countryCellTooltip, regionLabel } from './CountryMatrix.utilities';

const TOOLTIP_ID = 'promotions-tooltip';
const TH = 'whitespace-nowrap border-r border-gray-200 px-3 py-2.5 text-xs font-semibold text-gray-600';
const TD = 'whitespace-nowrap border-r border-gray-200 px-3 py-3 text-sm tabular-nums';

const Cell: React.FunctionComponent<{ row: PromotionCountryRow; cell: PromotionCountryCell; baseRegion: Region }> = ({ row, cell, baseRegion }) => {
	const tooltip = { 'data-tooltip-id': TOOLTIP_ID, 'data-tooltip-content': countryCellTooltip(row.hotelName, cell, baseRegion) };
	if (cell.discount == null || cell.discount === 0) {
		return <td className={`${TD} text-right text-gray-400`} {...tooltip}>{countryCellText(cell)}</td>;
	}
	const probable = cell.discount >= COUNTRY_RATE_THRESHOLD;
	return <td className={`${TD} text-right ${probable ? 'bg-emerald-50' : ''}`} {...tooltip}>
		<span className={probable ? 'font-semibold text-emerald-800' : 'text-gray-900'}>{countryCellText(cell)}</span>
		<span className={`mt-0.5 block text-xs ${probable ? 'text-gray-500' : 'text-gray-400'}`}>{countryCellPlans(cell)}</span>
	</td>;
};

const Row: React.FunctionComponent<{ row: PromotionCountryRow; index: number; baseRegion: Region }> = ({ row, index, baseRegion }) => {
	const background = row.main ? 'bg-sky-50' : index % 2 === 1 ? 'bg-gray-50' : 'bg-white';
	return <tr className={background}>
		<td className={`${TD} sticky left-0 z-10 ${background}`}>
			<span className={`flex w-52 items-center gap-1.5 ${row.main ? 'font-semibold' : 'font-medium'} text-gray-900`}>
				<span className="truncate" title={row.hotelName}>{row.hotelName}</span>
				{(row.preferredTier === 'PREFERRED' || row.preferredTier === 'PREFERRED_PLUS') &&
					<PreferredTierBadge plus={row.preferredTier === 'PREFERRED_PLUS'} />}
				{row.main && <Tag state="info">You</Tag>}
			</span>
		</td>
		<td className={`${TD} text-right text-xs font-medium tracking-wide text-gray-400`}>Base</td>
		{row.cells.map(cell => <Cell key={cell.region} row={row} cell={cell} baseRegion={baseRegion} />)}
	</tr>;
};

export const Legend: React.FunctionComponent = () =>
	<div className="flex flex-wrap items-center gap-5 border-t border-gray-200 px-5 py-3 text-xs text-gray-500">
		<span><span className="text-gray-900">10%</span> = a lower base rate for visitors of that country, on every rate plan</span>
		<span><span className="text-gray-900">10%*</span> = on some rate plans only</span>
		<span><span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-800">10%</span> = 5% or more: a probable country rate</span>
		<span><span className="font-semibold text-gray-400">—</span> = no consistent difference</span>
		<span>n/a = fewer than 3 compared nights</span>
	</div>;

export const countrySource = (currency: string, baseRegion: Region): string =>
	`Booking.com · web · public rates · 1 night · configured guest count · ${currency}. All enabled regions in one run; ${regionLabel(baseRegion)} is the base. `
	+ 'Rates are matched per room and rate plan; taxes and charges are included; deals are removed on both sides. '
	+ 'A probable country rate is a lower base rate observed for visitors from that country. Greece shares the European baseline.';

/** The matrix itself: hotels in rows, the base region first, then one column per booking.com region. */
const CountryMatrixTable: React.FunctionComponent<{ overview: PromotionCountryOverview }> = ({ overview }) => {
	const regions = overview.rows[0]?.cells.map(cell => cell.region) ?? overview.run?.regions.filter(region => region !== overview.baseRegion) ?? [];
	return <>
		<div className="overflow-x-auto">
			<table className="min-w-full border-t border-gray-300">
				<thead className="sticky-table-header">
					<tr>
						<th className={`${TH} sticky left-0 z-10 bg-white text-left`}>Hotel</th>
						<th className={`${TH} text-right`}>{regionLabel(overview.baseRegion)}</th>
						{regions.map(region => <th key={region} className={`${TH} text-right`}>{regionLabel(region)}</th>)}
					</tr>
				</thead>
				<tbody>
					{overview.rows.map((row, index) => <Row key={row.hotelId} row={row} index={index} baseRegion={overview.baseRegion} />)}
				</tbody>
			</table>
		</div>
		<Legend />
	</>;
};

export default CountryMatrixTable;
